import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertDialog, Button, Heading, IconButton, Notification, TextField, Tooltip } from '@lumx/react';
import { mdiAccountMultipleOutline, mdiFileImport, mdiMagnify } from '@mdi/js';
import { NodeType } from '../types';
import { useOrgData } from '../context/OrgDataContext';
import { useWizard } from '../context/WizardContext';
import { buildTree, collectAllNodeIds } from '../utils/treeUtils';
import { useDragDrop } from '../hooks/useDragDrop';
import { Icon } from './shared/icons';
import TreeRenderer from './tree/TreeRenderer';
import AddUnitForm from './tree/AddUnitForm';
import GroupsPage from './GroupsPage';
import DeleteNodeDialog from './dialogs/DeleteNodeDialog';
import ConvertToLocationDialog from './dialogs/ConvertToLocationDialog';

const OrgUnitsLocationsPage: React.FC = () => {
  const {
    nodes, addNode, renameNode, convertType, convertTypeForce, getDescendantIds,
    deleteNode, deleteNodeCascade, reparentNode, reorderNode,
    sites, mobileConfigs, draft,
  } = useOrgData();
  const { setNavGuard, pendingNavTarget, proceedNav, cancelNav } = useWizard();

  const [isDirty, setIsDirty] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const initialNodesRef = useRef(nodes);

  useEffect(() => {
    if (nodes === initialNodesRef.current) return;
    setIsDirty(true);
  }, [nodes]);

  useEffect(() => {
    setNavGuard(() => !isDirty);
    return () => setNavGuard(null);
  }, [isDirty, setNavGuard]);

  const displaySites = draft ? draft.sites : sites;

  const nodesWithDraftConfigs = useMemo(() => {
    if (!draft) return nodes;
    return nodes.map(n => {
      const draftNode = draft.nodes.find(dn => dn.id === n.id);
      return draftNode ? { ...n, mobileConfigId: draftNode.mobileConfigId } : n;
    });
  }, [nodes, draft]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isGlobalExpanded, setIsGlobalExpanded] = useState(true);
  const [orgSearch, setOrgSearch] = useState('');
  const [addingChildOf, setAddingChildOf] = useState<string | null | 'global' | 'none'>('none');
  const [deletingNodeId, setDeletingNodeId] = useState<string | null>(null);
  const [viewingGroups, setViewingGroups] = useState<{ kind: 'node' | 'site'; id: string; name: string } | null>(null);
  const [pendingConvertToLocation, setPendingConvertToLocation] = useState<{
    id: string; name: string; descendantIds: string[];
  } | null>(null);
  const [sitesExpandedIds, setSitesExpandedIds] = useState<Set<string>>(new Set());
  const toggleSites = useCallback((id: string) => setSitesExpandedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  }), []);

  const globalMobileConfigId = draft?.globalMobileConfigId ?? null;

  const nodesWithGlobal = useMemo(() => [
    { id: '__global__', name: 'Global / Platform', type: 'OrganizationalUnit' as const, parentId: null, mobileConfigId: globalMobileConfigId, createdAt: '', updatedAt: '' },
    ...nodesWithDraftConfigs,
  ], [nodesWithDraftConfigs, globalMobileConfigId]);

  const tree = useMemo(() => buildTree(nodesWithDraftConfigs, []), [nodesWithDraftConfigs]);

  const filteredTree = useMemo(() => {
    if (!orgSearch) return tree;
    const q = orgSearch.toLowerCase();
    function filterNode(n: any): any {
      const matchSelf = n.name.toLowerCase().includes(q);
      const filteredChildren = n.children.map(filterNode).filter(Boolean);
      if (matchSelf || filteredChildren.length > 0) return { ...n, children: filteredChildren };
      return null;
    }
    return tree.map(filterNode).filter(Boolean);
  }, [tree, orgSearch]);

  // ── DnD (OU reparenting only) ─────────────────────────────
  const dnd = useDragDrop({
    onSiteDropOnOU: () => {},
    onOUReparent: (ouId, newParentId) => {
      reparentNode(ouId, newParentId);
      setExpandedIds(prev => new Set([...prev, newParentId]));
    },
    onOUReorder: (ouId, targetId, position, targetParentId) => {
      reorderNode(ouId, targetId, position, targetParentId);
      if (targetParentId) setExpandedIds(prev => new Set([...prev, targetParentId]));
    },
    getNodes: () => nodes,
  });

  // ── Tree expand/collapse ──────────────────────────────────
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedIds(new Set(collectAllNodeIds(tree)));
    setIsGlobalExpanded(true);
  }, [tree]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  // ── Add node ──────────────────────────────────────────────
  const handleAddNodeSave = useCallback((name: string, type: NodeType, parentId: string | null) => {
    addNode(name, type, parentId);
    if (parentId) setExpandedIds(prev => new Set([...prev, parentId]));
    setAddingChildOf('none');
  }, [addNode]);

  // ── Delete node ───────────────────────────────────────────
  const handleDeleteNode = useCallback((id: string) => {
    deleteNodeCascade(id);
    setDeletingNodeId(null);
  }, [deleteNodeCascade]);

  // ── Convert type ──────────────────────────────────────────
  const handleConvertType = useCallback((id: string) => {
    const node = nodes.find(n => n.id === id);
    if (!node) return;
    if (node.type === 'OrganizationalUnit') {
      const descendantIds = getDescendantIds(id, nodes);
      if (descendantIds.length > 0) {
        setPendingConvertToLocation({ id, name: node.name, descendantIds });
        return;
      }
    }
    convertType(id, nodes);
  }, [convertType, getDescendantIds, nodes]);

  const handleConvertToLocationConfirm = useCallback(() => {
    if (!pendingConvertToLocation) return;
    convertTypeForce(pendingConvertToLocation.id);
    setPendingConvertToLocation(null);
  }, [pendingConvertToLocation, convertTypeForce]);

  const globalSiteCount = useMemo(
    () => displaySites.filter(s => s.assignedNodeId === '__global__').length,
    [displaySites],
  );

  // ── Groups drilldown ──────────────────────────────────────
  if (viewingGroups) {
    if (viewingGroups.kind === 'site') {
      return (
        <GroupsPage
          nodeId={viewingGroups.id}
          nodeName={viewingGroups.name}
          hasSitesMapped
          kind="site"
          onBack={() => setViewingGroups(null)}
        />
      );
    }
    const hasSitesMapped = viewingGroups.id === '__global__'
      ? true
      : displaySites.some(s => s.assignedNodeId === viewingGroups.id);
    return (
      <GroupsPage
        nodeId={viewingGroups.id}
        nodeName={viewingGroups.name}
        hasSitesMapped={hasSitesMapped}
        onBack={() => setViewingGroups(null)}
      />
    );
  }

  return (
    <div style={{ display: 'contents' }} onDragEnd={dnd.handleDragEnd}>
      <main className="main-content" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Page header */}
        <div className="page-header">
          <div className="page-header-left">
            <Heading as="h1" typography="headline">Org units &amp; Locations</Heading>
          </div>
          <div className="page-header-right">
            <Button emphasis="low" size="m" leftIcon={mdiFileImport}>Import JSON</Button>
            <Button emphasis="high" onClick={() => { setIsDirty(false); setShowSaveToast(true); setTimeout(() => setShowSaveToast(false), 3000); }}>Save</Button>
          </div>
        </div>

        {/* Full-width pane */}
        <div className="pane pane--org-units" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* Pane header: grey bar with label + actions */}
          <div className="pane-header">
            <div className="pane-title">
              <span className="pane-title-text">Org structure</span>
              <span className="pane-count">{nodes.length} units</span>
            </div>
            <div className="pane-header-actions">
              <button
                className="pane-action-btn"
                onClick={() => setAddingChildOf('global')}
                title="Add unit"
              >
                {Icon.plus} Add unit
              </button>
              <button className="pane-action-btn icon-only" onClick={expandAll} title="Expand all">
                {Icon.expandAll}
              </button>
              <button className="pane-action-btn icon-only" onClick={collapseAll} title="Collapse all">
                {Icon.collapseAll}
              </button>
            </div>
          </div>

          {/* Search — constrained to 1/3 of content width */}
          <div className="pane-search">
            <div style={{ width: '33%', minWidth: 200 }}>
              <TextField
                placeholder="Search org units &amp; locations…"
                value={orgSearch}
                onChange={setOrgSearch}
                icon={mdiMagnify}
                clearButtonProps={{ label: 'Clear' }}
              />
            </div>
          </div>

          {/* Tree */}
          <div className="pane-tree">
            {/* Column headers */}
            <div className="org-tree-thead">
              <div className="tree-th" />
              <div className="tree-th">Name</div>
              <div className="tree-th">
                Mobile config
                <Tooltip label="You can assign mobile configurations to Org units and Locations in 'Org mapping'.">
                  <span className="tooltip-icon" tabIndex={0}>{Icon.info}</span>
                </Tooltip>
              </div>
              <div className="tree-th">
                Sites
                <Tooltip label="You can assign 1 or more sites to Org units and Locations in 'Org mapping'.">
                  <span className="tooltip-icon" tabIndex={0}>{Icon.info}</span>
                </Tooltip>
              </div>
              <div className="tree-th" />
            </div>

            {/* Global / Platform root row */}
            <div
              className={`global-row ${dnd.dragOverId === '__global__' ? 'drag-over' : ''}`}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
              onDragLeave={dnd.handleDragLeave}
              onDrop={e => {
                e.preventDefault();
                const payload = dnd.getDragPayload();
                dnd.handleDragEnd();
                if (payload?.kind === 'ou') reparentNode(payload.id, null);
              }}
            >
              <div className="row-controls">
                <button
                  className={`expand-btn ${isGlobalExpanded ? 'expanded' : ''}`}
                  onClick={() => setIsGlobalExpanded(v => !v)}
                >
                  {Icon.chevronRight}
                </button>
              </div>
              <div className="global-row-name">
                <span className="global-icon">{Icon.globe}</span>
                Global / Platform
                <button
                  className="add-unit-btn"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => setAddingChildOf('global')}
                >
                  {Icon.plus} Add unit
                </button>
              </div>
              {/* Mobile config — read-only */}
              <div className="sites-col-text" style={{ padding: '0 var(--space-2)' }}>
                {globalMobileConfigId
                  ? mobileConfigs.find(c => c.id === globalMobileConfigId)?.name ?? '—'
                  : '—'}
              </div>
              {/* Sites count — read-only */}
              <div className="sites-col-text" style={{ padding: '0 var(--space-2)' }}>
                {globalSiteCount > 0 ? globalSiteCount : '—'}
              </div>
              {/* Actions column */}
              <div className="row-actions-cell">
                <IconButton
                  label="View groups"
                  icon={mdiAccountMultipleOutline}
                  emphasis="low"
                  size="s"
                  onClick={() => setViewingGroups({ kind: 'node', id: '__global__', name: 'Global / Platform' })}
                />
              </div>
            </div>

            {isGlobalExpanded && (
              <>
                {addingChildOf === 'global' && (
                  <div style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <AddUnitForm
                      parentId={null}
                      nodes={nodes}
                      onSave={handleAddNodeSave}
                      onCancel={() => setAddingChildOf('none')}
                    />
                  </div>
                )}

                {filteredTree.length > 0 ? (
                  <div className="children-box depth-0">
                    <TreeRenderer
                      nodes={filteredTree}
                      depth={1}
                      expandedIds={expandedIds}
                      addingChildOf={
                        addingChildOf === 'global' || addingChildOf === 'none' || addingChildOf === null
                          ? null
                          : addingChildOf
                      }
                      mobileConfigs={mobileConfigs}
                      allNodes={nodesWithGlobal}
                      allSites={displaySites}
                      sitesExpandedIds={sitesExpandedIds}
                      onViewSiteGroups={(siteId, siteName) => setViewingGroups({ kind: 'site', id: siteId, name: siteName })}
                      onSitesToggle={toggleSites}
                      onToggle={toggleExpand}
                      onRename={renameNode}
                      onConvertType={handleConvertType}
                      onAddChild={id => {
                        setAddingChildOf(id);
                        if (id) setExpandedIds(prev => new Set([...prev, id]));
                      }}
                      onDeleteRequest={setDeletingNodeId}
                      onAddChildSave={handleAddNodeSave}
                      onAddChildCancel={() => setAddingChildOf('none')}
                      onMobileConfigChange={() => {}}
                      onViewUsers={id => {
                        const node = nodesWithGlobal.find(n => n.id === id);
                        if (node) setViewingGroups({ kind: 'node', id, name: node.name });
                      }}
                      onDragStart={(e, kind, id) => dnd.handleDragStart(e, kind, id)}
                      onDragOver={dnd.handleOUDragOver}
                      onDrop={dnd.handleOUDrop}
                      onDragLeave={dnd.handleDragLeave}
                      dragOverId={dnd.dragOverId}
                      draggingId={dnd.draggingId}
                      dragInsert={dnd.dragInsert}
                      showSitesColumn
                      readOnlyAssignments
                    />
                  </div>
                ) : (
                  <div className="empty-tree">
                    {orgSearch
                      ? `No results for "${orgSearch}"`
                      : 'No units added yet. Use "Add unit" to create your first org unit or location.'}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Dialogs */}
      <ConvertToLocationDialog
        isOpen={!!pendingConvertToLocation}
        nodeName={pendingConvertToLocation?.name ?? ''}
        descendantCount={pendingConvertToLocation?.descendantIds.length ?? 0}
        onConfirm={handleConvertToLocationConfirm}
        onCancel={() => setPendingConvertToLocation(null)}
      />

      <DeleteNodeDialog
        node={deletingNodeId ? nodes.find(n => n.id === deletingNodeId) ?? null : null}
        allNodes={nodes}
        allSites={displaySites}
        onConfirm={() => deletingNodeId && handleDeleteNode(deletingNodeId)}
        onCancel={() => setDeletingNodeId(null)}
      />

      <AlertDialog
        isOpen={pendingNavTarget !== null}
        onClose={cancelNav}
        kind="warning"
        title="Unsaved changes"
        confirmProps={{ label: 'Leave without saving', onClick: proceedNav }}
        cancelProps={{ label: 'Go back', onClick: cancelNav }}
      >
        <p>You have unsaved changes on this page. Save before leaving or your progress won't be recorded.</p>
      </AlertDialog>

      <Notification type="success" isOpen={showSaveToast} content="Progress saved" />
    </div>
  );
};

export default OrgUnitsLocationsPage;
